export const MAX_ORDINAL_UPDATE_COUNT_BEFORE_CAP = 20

export type OrdinalStreamQueryData = {
    pages?: Array<{
        items?: Array<{ lens_stale?: boolean }>
        wallet_snapshot?: {
            status?: 'fresh' | 'stale' | 'partial'
            ordinal_count?: number
            indexed_count?: number
        } | null
    }>
}

type ErrorWithHttpStatus = {
    status?: number
    statusCode?: number
}

/**
 * Wallet snapshot unavailability is a deterministic service response until the
 * provider recovers. Let the user retry explicitly instead of multiplying the
 * same 503 through React Query's default retries, while retaining retries for
 * other transient failures.
 */
export function shouldRetryWalletSnapshotQuery(failureCount: number, error: unknown): boolean {
    const { status, statusCode } = (error || {}) as ErrorWithHttpStatus
    return status !== 503 && statusCode !== 503 && failureCount < 3
}

/**
 * Rows a live event retired from this lens — a sold listing on the Market tab,
 * an ordinal the viewer sold out of My Ordinals. They stay on screen with a
 * chip; counting them here is what turns the shared updates pill into the
 * refresh that finally prunes them.
 */
export function staleLensOrdinalCount(data: OrdinalStreamQueryData | undefined): number {
    if (!data?.pages?.length) return 0

    let count = 0
    for (const page of data.pages) {
        for (const item of page?.items || []) {
            if (!item?.lens_stale) continue
            count += 1
            if (count > MAX_ORDINAL_UPDATE_COUNT_BEFORE_CAP) {
                return MAX_ORDINAL_UPDATE_COUNT_BEFORE_CAP + 1
            }
        }
    }
    return count
}

export function unresolvedWalletOrdinalCount(data: OrdinalStreamQueryData | undefined): number {
    const snapshot = data?.pages?.[0]?.wallet_snapshot
    if (snapshot?.status !== 'fresh') return 0

    const ordinalCount = Number(snapshot.ordinal_count) || 0
    const indexedCount = Number(snapshot.indexed_count) || 0
    return Math.min(
        Math.max(ordinalCount - indexedCount, 0),
        MAX_ORDINAL_UPDATE_COUNT_BEFORE_CAP + 1,
    )
}
